import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Form, Input, Select, message, Switch, Popconfirm, Tooltip, Card, Tag } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, QuestionCircleOutlined, CalculatorOutlined } from '@ant-design/icons';
import api from '../../services/api';

const { TextArea } = Input;

const ForecastSettings = () => {
    const [settings, setSettings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingSettings, setEditingSettings] = useState(null);
    const [form] = Form.useForm();
    const [brands, setBrands] = useState([]);
    const [monthlyMetrics, setMonthlyMetrics] = useState([]);
    const formulaTextAreaRef = React.useRef(null);

    // Verileri yükle
    const loadData = async () => {
        try {
            setLoading(true);
            const [settingsResponse, brandsResponse, metricsResponse] = await Promise.all([
                api.get('/forecast'),
                api.get('/brands'),
                api.get('/monthly-metrics')
            ]);
            
            setSettings(settingsResponse.data || []);
            // Markaları displayOrder'a göre sırala
            const sortedBrands = (brandsResponse.data || []).sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
            setBrands(sortedBrands);
            setMonthlyMetrics(metricsResponse.data || []);
        } catch (error) {
            console.error('Veriler yüklenirken hata:', error);
            message.error('Veriler yüklenirken hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    // İmleç pozisyonuna metin ekleme
    const insertAtCursor = (text) => {
        const textArea = formulaTextAreaRef.current?.resizableTextArea?.textArea;
        if (!textArea) return;

        const start = textArea.selectionStart;
        const end = textArea.selectionEnd;
        const currentValue = form.getFieldValue('formula') || '';
        
        const newValue = currentValue.substring(0, start) + text + currentValue.substring(end);
        form.setFieldValue('formula', newValue);
        
        // İmleci eklenen metnin sonuna konumlandır
        setTimeout(() => {
            textArea.focus();
            textArea.setSelectionRange(start + text.length, start + text.length);
        }, 0);
    };

    // Forecast ayarı olmayan markaları bul
    const getAvailableBrands = () => {
        if (editingSettings) {
            // Düzenleme modunda tüm markalar
            return brands;
        }
        
        // Yeni oluşturma modunda sadece forecast ayarı olmayan markalar
        const brandsWithSettings = settings.map(s => s.brandId).filter(Boolean);
        return brands.filter(brand => !brandsWithSettings.includes(brand.id));
    };

    // Modal açma
    const openModal = (setting = null) => {
        setEditingSettings(setting);
        setModalVisible(true);
        
        if (setting) {
            form.setFieldsValue({
                brandId: setting.brandId,
                template: setting.template,
                formula: setting.formula,
                dependentMetrics: setting.dependentMetrics || [],
                description: setting.description,
                targetMetricId: setting.targetMetricId,
                autoSave: setting.autoSave,
                isActive: setting.isActive
            });
        } else {
            form.resetFields();
            form.setFieldsValue({
                autoSave: false,
                isActive: true,
                dependentMetrics: []
            });
        }
    };

    // Modal kapatma
    const closeModal = () => {
        setModalVisible(false);
        setEditingSettings(null);
        form.resetFields();
    };

    // Kaydet
    const handleSave = async (values) => {
        try {
            // Frontend-only alanları çıkar ve boş değerleri temizle
            const { settingType, ...cleanValues } = values;
            const payload = {
                ...cleanValues,
                targetMetricId: cleanValues.targetMetricId || null
            };
            
            if (editingSettings) {
                // Güncelleme
                await api.put(`/forecast/${editingSettings.id}`, payload);
                message.success('Forecast ayarı güncellendi');
            } else {
                // Yeni oluşturma
                if (payload.brandId) {
                    await api.post(`/forecast/brands/${payload.brandId}`, payload);
                } else {
                    await api.post(`/forecast/template/${payload.template}`, payload);
                }
                message.success('Yeni forecast ayarı oluşturuldu');
            }
            
            closeModal();
            await loadData();
        } catch (error) {
            console.error('Kaydetme hatası:', error);
            message.error('Kaydetme hatası: ' + (error.response?.data?.error || error.message));
        }
    };

    // Tekli hesaplama
    const handleCalculateSingle = async (record) => {
        try {
            const targetMonth = new Date();
            targetMonth.setMonth(targetMonth.getMonth() + 1); // Gelecek ay
            const targetMonthFormatted = targetMonth.toISOString().slice(0, 7); // YYYY-MM format
            
            await api.post(`/forecast/brands/${record.brandId}/calculate`, {
                targetMonth: targetMonthFormatted
            });
            
            message.success(`${record.Brand?.name || 'Marka'} için forecast hesaplandı`);
            await loadData();
        } catch (error) {
            console.error('Tekli hesaplama hatası:', error);
            message.error('Hesaplama hatası: ' + (error.response?.data?.error || error.message));
        }
    };

    // Silme
    const handleDelete = async (id) => {
        try {
            await api.delete(`/forecast/${id}`);
            message.success('Forecast ayarı silindi');
            await loadData();
        } catch (error) {
            console.error('Silme hatası:', error);
            message.error('Silme hatası: ' + (error.response?.data?.error || error.message));
        }
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 80
        },
        {
            title: 'Marka',
            dataIndex: ['Brand', 'name'],
            key: 'brandName',
            render: (text, record) => record.Brand?.name || 'Template Ayarı'
        },
        {
            title: 'Template',
            dataIndex: ['Brand', 'template'],
            key: 'template',
            render: (text, record) => record.Brand?.template || record.template || '-'
        },
        {
            title: 'Formül',
            dataIndex: 'formula',
            key: 'formula',
            render: (text) => (
                <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                    {text}
                </span>
            )
        },
        {
            title: 'Hedef Metrik',
            dataIndex: 'targetMetricId',
            key: 'targetMetricId',
            render: (targetMetricId) => {
                if (!targetMetricId) return '-';
                const metric = monthlyMetrics.find(m => m.id === targetMetricId);
                return metric?.name || `ID: ${targetMetricId}`;
            }
        },
        {
            title: 'Otomatik Kayıt',
            dataIndex: 'autoSave',
            key: 'autoSave',
            render: (autoSave) => (
                <Switch checked={autoSave} disabled size="small" />
            ),
            width: 120
        },
        {
            title: 'Aktif',
            dataIndex: 'isActive',
            key: 'isActive',
            render: (isActive) => (
                <Switch checked={isActive} disabled size="small" />
            ),
            width: 80
        },
        {
            title: 'İşlemler',
            key: 'actions',
            width: 150,
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="Forecast hesapla">
                        <Button
                            icon={<CalculatorOutlined />}
                            size="small"
                            onClick={() => handleCalculateSingle(record)}
                            type="primary"
                            ghost
                        />
                    </Tooltip>
                    <Button
                        icon={<EditOutlined />}
                        size="small"
                        onClick={() => openModal(record)}
                    />
                    <Popconfirm
                        title="Bu forecast ayarını silmek istediğinize emin misiniz?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Evet"
                        cancelText="Hayır"
                    >
                        <Button
                            icon={<DeleteOutlined />}
                            size="small"
                            danger
                        />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <div>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                <h3>Forecast Ayarları</h3>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => openModal()}
                >
                    Yeni Ayar
                </Button>
            </div>

            <Table
                columns={columns}
                dataSource={settings}
                loading={loading}
                rowKey="id"
                size="small"
                scroll={{ x: 1000 }}
            />

            <Modal
                title={editingSettings ? 'Forecast Ayarını Düzenle' : 'Yeni Forecast Ayarı'}
                open={modalVisible}
                onCancel={closeModal}
                footer={null}
                width={600}
            >
                <Form
                    form={form}
                    onFinish={handleSave}
                    layout="vertical"
                >
                    <Form.Item
                        label="Ayar Türü"
                        name="settingType"
                        initialValue="brand"
                    >
                        <Select disabled={!!editingSettings}>
                            <Select.Option value="brand">Marka Bazında</Select.Option>
                            <Select.Option value="template">Template Bazında</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) => 
                            prevValues.settingType !== currentValues.settingType
                        }
                    >
                        {({ getFieldValue }) => {
                            const settingType = getFieldValue('settingType');
                            
                            if (settingType === 'brand') {
                                return (
                                    <Form.Item
                                        label="Marka"
                                        name="brandId"
                                        rules={[{ required: true, message: 'Marka seçin' }]}
                                    >
                                        <Select
                                            placeholder="Marka seçin"
                                            showSearch
                                            filterOption={(input, option) =>
                                                option.children.toLowerCase().includes(input.toLowerCase())
                                            }
                                        >
                                            {getAvailableBrands().map(brand => (
                                                <Select.Option key={brand.id} value={brand.id}>
                                                    {brand.name} ({brand.template})
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                );
                            } else {
                                return (
                                    <Form.Item
                                        label="Template"
                                        name="template"
                                        rules={[{ required: true, message: 'Template seçin' }]}
                                    >
                                        <Select placeholder="Template seçin">
                                            <Select.Option value="WEB AUTOBUDGET">WEB AUTOBUDGET</Select.Option>
                                            <Select.Option value="WEB MANUELBUDGET">WEB MANUELBUDGET</Select.Option>
                                            <Select.Option value="TRENDYOL">TRENDYOL</Select.Option>
                                        </Select>
                                    </Form.Item>
                                );
                            }
                        }}
                    </Form.Item>

                    <Form.Item
                        label={
                            <Space>
                                Forecast Formülü
                                <Tooltip 
                                    title={
                                        <div>
                                            <div><strong>Geçmiş Veri Fonksiyonları:</strong></div>
                                            <div>• <code>last1(metrik)</code> - 1 ay önceki değer</div>
                                            <div>• <code>last3(metrik)</code> - 3 ay önceki değer</div>
                                            <div>• <code>last6(metrik)</code> - 6 ay önceki değer</div>
                                            <div>• <code>last12(metrik)</code> - 12 ay önceki değer</div>
                                            <div><strong>Ortalama Fonksiyonları:</strong></div>
                                            <div>• <code>monthlyAvg(metrik, 3)</code> - 3 ay ortalaması</div>
                                            <div>• <code>avgRange(metrik, 1, 6)</code> - 1-6 ay arası ortalama</div>
                                            <div><strong>Matematik Fonksiyonları:</strong></div>
                                            <div>• <code>sum(metrik1, metrik2)</code> - Toplama</div>
                                            <div>• <code>avg(metrik1, metrik2)</code> - Ortalama</div>
                                            <div><strong>Diğer Fonksiyonlar:</strong></div>
                                            <div>• <code>monthlyChange(metrik)</code> - Aylık değişim</div>
                                            <div>• <code>lastDays(metrik, 7)</code> - Son N günün toplamı</div>
                                            <div>• <code>divByDaysInMonth(metrik)</code> - Ay gün sayısına böl</div>
                                            <div><strong>Örnekler:</strong></div>
                                            <div>• <code>last1(TAHMINI_CIRO) * 1.1</code></div>
                                            <div>• <code>monthlyAvg(TAHMINI_CIRO, 3) * 1.05</code></div>
                                            <div>• <code>avgRange(REKLAM_GIDERI, 1, 6) + last1(TAHMINI_CIRO)</code></div>
                                        </div>
                                    }
                                    overlayStyle={{ maxWidth: '400px' }}
                                >
                                    <QuestionCircleOutlined style={{ color: '#1890ff' }} />
                                </Tooltip>
                            </Space>
                        }
                        name="formula"
                        rules={[{ required: true, message: 'Formül girin' }]}
                    >
                        <div>
                            <TextArea
                                ref={formulaTextAreaRef}
                                rows={3}
                                placeholder="Örn: last1(TAHMINI_CIRO) * 1.1 + monthlyAvg(REKLAM_GIDERI, 3)"
                                style={{ fontFamily: 'monospace' }}
                            />
                            <div style={{ marginTop: 8 }}>
                                <Space wrap>
                                    {['+', '-', '*', '/', '(', ')'].map(op => (
                                        <Button
                                            key={op}
                                            size="small"
                                            onClick={() => insertAtCursor(op)}
                                            style={{ minWidth: 32 }}
                                        >
                                            {op}
                                        </Button>
                                    ))}
                                </Space>
                            </div>
                        </div>
                    </Form.Item>

                    <Form.Item
                        label="Bağımlı Metrikler"
                        name="dependentMetrics"
                        tooltip="Formülde kullanılan metrikleri seçin"
                    >
                        <Select
                            mode="multiple"
                            placeholder="Formülde kullanılan metrikleri seçin"
                            style={{ width: '100%' }}
                            showSearch
                            filterOption={(input, option) =>
                                option.children.toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {monthlyMetrics.map(metric => (
                                <Select.Option key={metric.id} value={metric.id}>
                                    {metric.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="Açıklama"
                        name="description"
                    >
                        <Input placeholder="Bu forecast ayarının açıklaması" />
                    </Form.Item>

                    <Form.Item
                        label="Hedef Metrik (Sonuçları kaydetmek için)"
                        name="targetMetricId"
                    >
                        <Select
                            placeholder="Opsiyonel - Sonuçların kaydedileceği metrik"
                            allowClear
                        >
                            {monthlyMetrics.map(metric => (
                                <Select.Option key={metric.id} value={metric.id}>
                                    {metric.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="Otomatik Kayıt"
                        name="autoSave"
                        valuePropName="checked"
                    >
                        <Switch />
                    </Form.Item>

                    <Form.Item
                        label="Aktif"
                        name="isActive"
                        valuePropName="checked"
                    >
                        <Switch />
                    </Form.Item>

                    <Form.Item style={{ marginTop: 24, marginBottom: 0 }}>
                        <Space style={{ float: 'right' }}>
                            <Button onClick={closeModal}>
                                İptal
                            </Button>
                            <Button type="primary" htmlType="submit">
                                {editingSettings ? 'Güncelle' : 'Oluştur'}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default ForecastSettings;